import { FC } from "react"
import { useTranslation } from "react-i18next"
import { showToast } from "@/helpers/toast"
import { useOtp } from "@/hooks/useOtp"
import { useModal } from "@/modals"
import { useVerifyWithdrawalMutation } from "@/store/apis/withdrawalAPI"
import { Button } from "@/ui/Button"
import { Modal } from "@/ui/Modal"
import { OutlinedText } from "@/ui/OutlinedText"
import styles from "./WithdrawOtpModal.module.scss"

const modalType = "withdrawOtp" as const
const OTP_LENGTH = 6

export const WithdrawOtpModal: FC = () => {
  const { t } = useTranslation()
  const { modal, getModalParamsRequired } = useModal(modalType)
  const params = getModalParamsRequired(modalType)
  const { otp, otpValue, otpRefs, handleChange, handleKeyDown, handlePaste } =
    useOtp(OTP_LENGTH)
  const [verifyWithdrawal, { isLoading: isVerifying }] =
    useVerifyWithdrawalMutation()

  const handleConfirm = async (): Promise<void> => {
    try {
      await verifyWithdrawal({ code: otpValue }).unwrap()
      modal.close()
      params.onSuccess()
    } catch {
      showToast.error(t("wallet.withdraw.otpModal.verifyError"))
    }
  }

  const handleCancel = (): void => {
    modal.close()
  }

  return (
    <Modal.Center type={modalType}>
      <Modal.StoneFlexContainer
        content={{ className: styles.WithdrawOtpModal_content }}
      >
        <OutlinedText className={styles.WithdrawOtpModal_title}>
          {t("wallet.withdraw.otpModal.title")}
        </OutlinedText>
        <p className={styles.WithdrawOtpModal_description}>
          {t("wallet.withdraw.otpModal.description")}
        </p>
        <div className={styles.WithdrawOtpModal_otpGroup}>
          {otp.map((digit, index) => (
            <input
              key={`otp-${index}`}
              ref={(el) => {
                otpRefs.current[index] = el
              }}
              className={styles.WithdrawOtpModal_otpBox}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              autoFocus={index === 0}
            />
          ))}
        </div>
        <div className={styles.WithdrawOtpModal_buttons}>
          <Button
            type="button"
            className={styles.WithdrawOtpModal_button}
            styles="green"
            onClick={handleCancel}
            disabled={isVerifying}
          >
            {t("wallet.withdraw.otpModal.cancelButton")}
          </Button>
          <Button
            type="button"
            className={styles.WithdrawOtpModal_button}
            styles="orange"
            onClick={handleConfirm}
            isLoading={isVerifying}
            disabled={otpValue.length !== OTP_LENGTH}
          >
            {t("wallet.withdraw.otpModal.confirmButton")}
          </Button>
        </div>
      </Modal.StoneFlexContainer>
    </Modal.Center>
  )
}
